/* eslint-disable react-refresh/only-export-components -- same colocated-API
 * pattern as lib/tableLayout.tsx: the provider ships alongside the hook +
 * types consumers need. */
/**
 * Wave 3 · B7 — TabsProvider: the source of truth for which per-record tabs
 * are open in the bottom pill bar.
 *
 * Design (see AppRoutes.tsx + TabsHost.tsx docblocks for the rendering half):
 * the REAL browser URL (react-router's live location, from the single
 * top-level BrowserRouter in App.tsx) always drives the ACTIVE tab — there is
 * no separate "activeId" piece of state to fall out of sync with it.
 * `activeKey` is derived every render straight from `useLocation()` via the
 * same `deriveTabKey` used to identify tabs. A `useEffect` watches the live
 * location and reconciles the persisted `tabs` array:
 *
 *   - location's key matches an existing tab  → update that tab's stored
 *     location snapshot + lastActiveAt (covers both "switching to an already-
 *     open tab" and "navigating within the active tab, e.g. a sort/filter
 *     query change" — same tab, just a fresher snapshot).
 *   - no match                                → open a new tab, applying the
 *     MAX_TABS/LRU rule below.
 *
 * Activating a tab (clicking its pill) is just `navigate(tab.location)` —
 * the effect above then recognizes the resulting location and marks that
 * tab's lastActiveAt fresh. Closing/pinning/reordering mutate the `tabs`
 * array directly (closing the active tab also navigates elsewhere, since
 * there must always be a valid active location).
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react'
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { MAX_TABS, type TabLocationSnapshot, type TabRecord } from './types'
import { deriveTabKey, deriveTabTitle } from './tabIdentity'
import { reconcileTabs } from './reconcile'
import { forgetScroll } from './scrollMemory'
import { loadTabs, saveTabs } from './persistence'

function snapshotOf(pathname: string, search: string, hash: string): TabLocationSnapshot {
  return { pathname, search, hash }
}

function locationHref(loc: TabLocationSnapshot): string {
  return `${loc.pathname}${loc.search}${loc.hash}`
}

interface TabsApi {
  tabs: TabRecord[]
  activeKey: string
  activate: (id: string) => void
  close: (id: string) => void
  togglePin: (id: string) => void
  move: (id: string, direction: -1 | 1) => void
  /** Set when the MAX_TABS/all-pinned refusal fires, so the bar can show a
   *  one-line explanation. Owner dismisses it (or it's replaced by the next
   *  notice); purely informational, nothing blocks on it. */
  notice: string | null
  dismissNotice: () => void
}

const Ctx = createContext<TabsApi | null>(null)

export function TabsProvider({ children }: { children: ReactNode }) {
  const location = useLocation()
  const navigate = useNavigate()
  const [tabs, setTabs] = useState<TabRecord[]>(() => loadTabs())
  const [notice, setNotice] = useState<string | null>(null)
  // The key that was active going INTO the current navigation — used by the
  // "all 8 tabs pinned" fallback to know which tab slot to repurpose ("replace
  // active" per the spec) instead of refusing to render anything.
  const prevActiveKeyRef = useRef<string | null>(null)

  const activeKey = deriveTabKey(location.pathname, location.search)
  const navigationType = useNavigationType()
  // The key the tabs were last reconciled to: what a REPLACE navigation replaced.
  const lastKeyRef = useRef<string | null>(null)

  // Latest tabs, readable from callbacks without making them depend on
  // `tabs` (which would re-create them, and — more importantly — lets
  // `activate`/`close` read state without performing navigation from inside
  // a setState updater, where React (StrictMode double-invoke) may call the
  // updater more than once per commit).
  const tabsRef = useRef(tabs)
  useEffect(() => { tabsRef.current = tabs }, [tabs])

  // Persist on every change — always-on localStorage backing, same as
  // lib/tableLayout.tsx's saveLocal-on-effect pattern.
  useEffect(() => { saveTabs(tabs) }, [tabs])

  // Reconcile the tabs list against the live browser location. The rule
  // itself lives in reconcile.ts (pure, unit-tested); this effect only feeds
  // it the live location and publishes its notice.
  useEffect(() => {
    const snap = snapshotOf(location.pathname, location.search, location.hash)
    const key = activeKey
    const replacedKey = navigationType === 'REPLACE' && lastKeyRef.current !== key
      ? lastKeyRef.current
      : null
    lastKeyRef.current = key
    if (replacedKey) forgetScroll(replacedKey)
    setTabs((cur) => {
      const r = reconcileTabs({
        cur, key, snap,
        title: deriveTabTitle(location.pathname, location.search),
        now: Date.now(),
        maxTabs: MAX_TABS,
        prevActiveKey: prevActiveKeyRef.current,
        replacedKey,
      })
      if (r.tabs === cur) return cur
      prevActiveKeyRef.current = key
      if (r.notice) setNotice(r.notice)
      return r.tabs as TabRecord[]
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, location.hash])

  const activate = useCallback((id: string) => {
    const t = tabsRef.current.find((x) => x.id === id)
    if (t) navigate(locationHref(t.location))
  }, [navigate])

  const close = useCallback((id: string) => {
    const cur = tabsRef.current
    const idx = cur.findIndex((t) => t.id === id)
    if (idx === -1) return
    const next = cur.filter((t) => t.id !== id)
    setTabs(next)
    forgetScroll(id)
    if (id === activeKey) {
      // Closing the tab we're looking at — jump to the most-recently-active
      // survivor, or home if this was the last tab.
      const target = next.length > 0
        ? next.reduce((a, b) => (b.lastActiveAt > a.lastActiveAt ? b : a))
        : null
      navigate(target ? locationHref(target.location) : '/overview')
    }
  }, [activeKey, navigate])

  const togglePin = useCallback((id: string) => {
    setTabs((cur) => cur.map((t) => (t.id === id ? { ...t, pinned: !t.pinned } : t)))
  }, [])

  const move = useCallback((id: string, direction: -1 | 1) => {
    setTabs((cur) => {
      const idx = cur.findIndex((t) => t.id === id)
      const swapIdx = idx + direction
      if (idx === -1 || swapIdx < 0 || swapIdx >= cur.length) return cur
      const next = [...cur]
      const tmp = next[idx]
      next[idx] = next[swapIdx]
      next[swapIdx] = tmp
      return next
    })
  }, [])

  const dismissNotice = useCallback(() => setNotice(null), [])

  const api = useMemo<TabsApi>(() => ({
    tabs, activeKey, activate, close, togglePin, move, notice, dismissNotice,
  }), [tabs, activeKey, activate, close, togglePin, move, notice, dismissNotice])

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}

export function useTabs(): TabsApi {
  const v = useContext(Ctx)
  if (!v) throw new Error('useTabs must be used within a TabsProvider')
  return v
}

export { locationHref, snapshotOf }
